/**
 * iCalendar (.ics) export for maintenance reminders.
 * Pure string building — no dependencies, works in the browser.
 */

export interface IcsReminder {
  /** Event title, e.g. "Toyota Camry — Engine oil & filter". */
  title: string;
  /** All-day event date. */
  date: Date;
  description?: string;
  /** Days before the event to alarm. Default 7. */
  alarmDaysBefore?: number;
}

const pad = (n: number) => String(n).padStart(2, "0");

function ymd(d: Date) {
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}`;
}

function stamp(d: Date) {
  return `${ymd(d)}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

function esc(s: string) {
  return s
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/** Fold lines at 75 octets per RFC 5545. */
function fold(line: string) {
  if (line.length <= 75) return line;
  const parts: string[] = [];
  let rest = line;
  parts.push(rest.slice(0, 75));
  rest = rest.slice(75);
  while (rest.length > 74) {
    parts.push(" " + rest.slice(0, 74));
    rest = rest.slice(74);
  }
  if (rest.length) parts.push(" " + rest);
  return parts.join("\r\n");
}

export function buildIcs(reminders: IcsReminder[], calendarName: string) {
  const now = new Date();
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//AutoSpecData//Maintenance Reminders//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    `X-WR-CALNAME:${esc(calendarName)}`,
  ];

  reminders.forEach((r, i) => {
    const end = new Date(r.date.getTime() + 24 * 60 * 60 * 1000);
    lines.push(
      "BEGIN:VEVENT",
      `UID:${ymd(r.date)}-${i}-${Math.random().toString(36).slice(2, 10)}@specbase`,
      `DTSTAMP:${stamp(now)}`,
      `DTSTART;VALUE=DATE:${ymd(r.date)}`,
      `DTEND;VALUE=DATE:${ymd(end)}`,
      `SUMMARY:${esc(r.title)}`,
    );
    if (r.description) lines.push(`DESCRIPTION:${esc(r.description)}`);
    lines.push(
      "TRANSP:TRANSPARENT",
      "BEGIN:VALARM",
      `TRIGGER:-P${r.alarmDaysBefore ?? 7}D`,
      "ACTION:DISPLAY",
      `DESCRIPTION:${esc(r.title)}`,
      "END:VALARM",
      "END:VEVENT",
    );
  });

  lines.push("END:VCALENDAR");
  return lines.map(fold).join("\r\n") + "\r\n";
}

export function downloadIcs(
  reminders: IcsReminder[],
  calendarName: string,
  filename: string,
) {
  const blob = new Blob([buildIcs(reminders, calendarName)], {
    type: "text/calendar;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Convert a mileage gap into a calendar date using an annual mileage estimate. */
export function dateForMiles(milesFromNow: number, annualMiles: number) {
  const days = Math.max(1, Math.round((milesFromNow / Math.max(annualMiles, 1)) * 365));
  const d = new Date();
  d.setUTCHours(9, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

/** Convert a month-based interval into a calendar date. */
export function dateForMonths(months: number) {
  const d = new Date();
  d.setUTCHours(9, 0, 0, 0);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d;
}
